import { env } from "./env";

export type RuntimeConfig = {
  keycloakUrl?: string;
  keycloakRealm?: string;
  keycloakClientId?: string;
  /** Public origin of the notification worker. Empty keeps the socket on the page. */
  notificationServiceBaseUrl?: string;
};

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Prefer pod-injected `/runtime-config.js` (per-stage Keycloak and the
 * notification worker). Fall back to bake-time Vite env for `npm run dev`
 * and images that have not yet been redeployed with the runtime endpoint.
 */
export const getRuntimeConfig = (): RuntimeConfig => {
  if (typeof window !== "undefined" && window.__RUNTIME_CONFIG__) {
    return window.__RUNTIME_CONFIG__;
  }

  return {};
};

export const resolveKeycloakUrl = (): string => {
  const fromRuntime = getRuntimeConfig().keycloakUrl?.trim();
  if (fromRuntime) {
    return fromRuntime;
  }

  return env.VITE_KEYCLOAK_URL;
};

export const resolveKeycloakRealm = (): string => {
  const fromRuntime = getRuntimeConfig().keycloakRealm?.trim();
  if (fromRuntime) {
    return fromRuntime;
  }

  return env.VITE_KEYCLOAK_REALM;
};

export const resolveKeycloakClientId = (): string => {
  const fromRuntime = getRuntimeConfig().keycloakClientId?.trim();
  if (fromRuntime) {
    return fromRuntime;
  }

  return env.VITE_KEYCLOAK_CLIENT_ID;
};

const httpServiceBase = (value: string): string => {
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return "";
    }

    return value;
  } catch {
    return "";
  }
};

/**
 * Chooses the notification host. A missing runtime value keeps the bake-time
 * URL. An empty runtime value stays empty, so the socket uses the page origin.
 * A value that is not an http(s) URL is dropped.
 */
export const notificationServiceBaseFrom = (
  runtimeValue: string | undefined,
  bakedValue: string,
): string => {
  if (runtimeValue === undefined) {
    return bakedValue;
  }

  const trimmed = runtimeValue.trim();
  if (!trimmed) {
    return "";
  }

  return httpServiceBase(trimmed);
};

/**
 * Host for `/ws/notifications`.
 *
 * Pod env arrives through `/runtime-config.js`, so one image can point at
 * each stage's notification worker. Dev stays on the page origin: the Vite
 * `/ws` proxy serves the socket there. Do not pass the result through
 * `resolveServiceBaseUrl`. That helper drops any host that is not the page,
 * which is what sent QA sockets to the UI instead of the worker.
 */
export const resolveNotificationServiceBaseUrl = (): string => {
  if (import.meta.env.DEV) {
    return "";
  }

  return notificationServiceBaseFrom(
    getRuntimeConfig().notificationServiceBaseUrl,
    env.VITE_NOTIFICATION_SERVICE_BASE_URL,
  );
};
